import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logo from "../traderhub24-logo.svg";
import Footer from "./components/Footer";
import { supabase } from './lib/supabase';

interface LandingPageProps {
  onNavigate?: (page: "home" | "pricing" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => void;
  onRegister?: () => void;
  onLogin?: () => void;
}

interface CTAButton {
  text: string;
  tag: string;
}

const CTA_BUTTONS: CTAButton[] = [
  { text: "Start Tracking Your First Trade", tag: "start_tracking" },
  { text: "Get My Free Trading Journal", tag: "free_journal" },
  { text: "Show Me My Trading Weaknesses", tag: "trading_weaknesses" },
  { text: "Start for Free", tag: "start_free" },
  { text: "Create My Trading Account", tag: "create_account" },
  { text: "Begin My Trading Journey", tag: "begin_journey" },
];

const COMPETITORS = ["Excel", "Google Sheets", "Tradingview", "Your Notebook", "Others"];

function LandingPage({ onNavigate, onRegister, onLogin }: LandingPageProps) {
  const { t } = useTranslation(['common', 'pages']);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedCTA, setSelectedCTA] = useState<CTAButton>(CTA_BUTTONS[0]);
  const [currentCompetitor, setCurrentCompetitor] = useState(0);
  const [videoPopupSrc, setVideoPopupSrc] = useState<string | null>(null);

  // Select random CTA on page load
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * CTA_BUTTONS.length);
    setSelectedCTA(CTA_BUTTONS[randomIndex]);
  }, []);

  // Rotate competitor names every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCompetitor((prev) => (prev + 1) % COMPETITORS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Track CTA button click
  const handleCTAClick = async () => {
    try {
      // Store in localStorage for immediate tracking
      const trackingData = {
        tag: selectedCTA.tag,
        text: selectedCTA.text,
        timestamp: new Date().toISOString(),
        page: 'landing',
      };
      
      // Store in localStorage
      const existingData = JSON.parse(localStorage.getItem('cta_clicks') || '[]');
      existingData.push(trackingData);
      localStorage.setItem('cta_clicks', JSON.stringify(existingData));

      // Try to send to Supabase (non-blocking)
      try {
        await supabase.from('cta_tracking').insert({
          tag: selectedCTA.tag,
          button_text: selectedCTA.text,
          page: 'landing',
          clicked_at: new Date().toISOString(),
        });
      } catch (supabaseError) {
        // Silently fail if table doesn't exist yet or user is not authenticated
        console.debug('CTA tracking to Supabase failed (this is OK):', supabaseError);
      }
    } catch (error) {
      console.error('Error tracking CTA click:', error);
    }

    // Call the original onRegister handler
    if (onRegister) {
      onRegister();
    }
  };


  const featureSections = [
    { key: 'tradeTracking', imageId: '/blog/add-trade.webm' },
    { key: 'customizableDashboard', imageId: '/blog/customizable-dashboard.webm' },
    { key: 'tradeJournal', imageId: 'trade-journal' },
    { key: 'performanceAnalytics', imageId: 'performance-analytics' },
    { key: 'eodUpdates', imageId: 'eod-updates' },
    { key: 'socialTrade', imageId: 'social-trade' },
  ] as const;

  return (
    <div className="min-h-screen bg-app-box text-gray-900 font-manrope">
      {/* Header */}
      <header className="w-full py-3 md:py-4 px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-10 md:h-14 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
          />
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <a 
              href="/tools"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/tools';
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:nav.tools')}
            </a>
            <a 
              href="/pricing"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/pricing';
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('pages:landing.pricing')}
            </a>
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (onNavigate) onNavigate("documentation");
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:footer.documentation')}
            </a>
            <a
              href="/blog"
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:footer.blog')}
            </a>
            <button
              onClick={onLogin}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:header.signIn')}
            </button>
            <button
              onClick={onRegister}
              className="px-6 py-2.5 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
            >
              Start for Free
            </button>
          </nav>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {showMobileMenu ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        {/* Mobile Menu */}
        {showMobileMenu && (
          <nav className="md:hidden mt-4 pb-4 border-b border-gray-200 animate-in slide-in-from-top duration-200">
            <div className="flex flex-col gap-2">
               <a 
                 href="/tools"
                 onClick={(e) => {
                   e.preventDefault();
                   setShowMobileMenu(false);
                   window.location.href = '/tools';
                 }}
                 className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
               >
                 {t('common:nav.tools')}
               </a>
              <a 
                href="/pricing"
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileMenu(false);
                  window.location.href = '/pricing';
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('pages:landing.pricing')}
              </a>
              <a 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileMenu(false);
                  if (onNavigate) onNavigate("documentation");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('common:footer.documentation')}
              </a>
              <a
                href="/blog"
                onClick={() => setShowMobileMenu(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('common:footer.blog')}
              </a>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  if (onLogin) onLogin();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                {t('common:header.signIn')}
              </button>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  handleCTAClick();
                }}
                className="px-4 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors text-sm mt-2"
              >
                {selectedCTA.text}
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 lg:py-20">
        {/* Hero Section - Centered */}
        <section className="text-center max-w-4xl mx-auto mb-16 md:mb-24">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-6">
            {(() => {
              const fullTitle = t('pages:landing.heroTitle');
              const highlight = t('pages:landing.heroTitleHighlight');
              const idx = fullTitle.indexOf(highlight);
              if (idx === -1) return fullTitle;
              return (
                <>
                  {fullTitle.slice(0, idx)}
                  <span className="text-orange-500">{highlight}</span>
                  {fullTitle.slice(idx + highlight.length)}
                </>
              );
            })()}
          </h1>
          <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
            {t('pages:landing.heroSubtitle')}
          </p>
          <button
            onClick={handleCTAClick}
            className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors shadow-md text-sm md:text-base"
          >
            {t('pages:landing.signUpForFree')}
            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
          <p className="mt-3 text-xs md:text-sm text-gray-500">
            {t('pages:landing.noCreditCard')}
          </p>
          {/* Social Proof */}
          <div className="mt-10 md:mt-14 flex flex-col items-center gap-4">
            <div className="flex -space-x-3">
              {['JD', 'MK', 'AS', 'PL', 'RW', 'CT'].map((initials, i) => {
                const colors = ['f97316', 'ea580c', 'c2410c', '9a3412', '7c2d12', 'dc2626'];
                return (
                  <img
                    key={i}
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${colors[i]}&color=fff&size=40`}
                    alt=""
                    className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    style={{ zIndex: 7 - i }}
                  />
                );
              })}
            </div>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <svg key={i} className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-sm md:text-base text-gray-600 max-w-xl">
              {t('pages:landing.socialProof')}
            </p>
          </div>
        </section>

        {/* Features Section - Each feature as separate block with image placeholder */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('pages:landing.featuresSectionTitle')}</h2>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
            {t('pages:landing.featuresSectionSubtitle')}
          </p>
        </div>
        {featureSections.map(({ key, imageId }, index) => (
          <section key={key} className="py-12 md:py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {t(`pages:landing.features.${key}.title`)}
                </h3>
                <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                  {t(`pages:landing.features.${key}.description`)}
                </p>
              </div>
              <div className={`aspect-video rounded-xl bg-gray-200 overflow-hidden ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                {imageId.match(/\.(webm|mp4)$/i) ? (
                  <video
                    src={imageId}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover rounded-xl cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => setVideoPopupSrc(imageId)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                    <span>Feature image: {imageId}</span>
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* Trust Badges Section */}
        <section className="pt-8 md:pt-12 pb-4 md:pb-6 mt-6 md:mt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto">
            <div className="flex flex-col items-center justify-center p-4 md:p-6 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <span className="text-xs md:text-sm font-medium text-gray-700 text-center">Bank-level encryption</span>
            </div>
            <div className="flex flex-col items-center justify-center p-4 md:p-6 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span className="text-xs md:text-sm font-medium text-gray-700 text-center">GDPR Compliant</span>
            </div>
            <div className="flex flex-col items-center justify-center p-4 md:p-6 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <span className="text-xs md:text-sm font-medium text-gray-700 text-center">Your data is never shared</span>
            </div>
            <div className="flex flex-col items-center justify-center p-4 md:p-6 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span className="text-xs md:text-sm font-medium text-gray-700 text-center">SSL Secured</span>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-8 md:py-12 lg:py-20 mt-6 md:mt-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('pages:landing.howItWorks.title')}</h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
              {t('pages:landing.howItWorks.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('pages:landing.howItWorks.step1.title')}</h3>
              <p className="text-gray-600 text-sm md:text-base">
                {t('pages:landing.howItWorks.step1.description')}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('pages:landing.howItWorks.step2.title')}</h3>
              <p className="text-gray-600 text-sm md:text-base">
                {t('pages:landing.howItWorks.step2.description')}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('pages:landing.howItWorks.step3.title')}</h3>
              <p className="text-gray-600 text-sm md:text-base">
                {t('pages:landing.howItWorks.step3.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Key Benefits Section */}
        <section className="py-8 md:py-12 lg:py-20 bg-white">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('pages:landing.whyChoose.title')}</h2>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
              {t('pages:landing.whyChoose.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages:landing.whyChoose.comprehensiveTracking.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  {t('pages:landing.whyChoose.comprehensiveTracking.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages:landing.whyChoose.advancedAnalytics.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  {t('pages:landing.whyChoose.advancedAnalytics.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages:landing.whyChoose.tradeJournal.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  {t('pages:landing.whyChoose.tradeJournal.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages:landing.whyChoose.socialTrading.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  {t('pages:landing.whyChoose.socialTrading.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages:landing.whyChoose.strategyManagement.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  {t('pages:landing.whyChoose.strategyManagement.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages:landing.whyChoose.automaticUpdates.title')}</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  {t('pages:landing.whyChoose.automaticUpdates.description')}
                </p>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="mt-12 md:mt-16">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Better than <span className="text-orange-600 inline-block min-w-[200px] transition-opacity duration-500">{COMPETITORS[currentCompetitor]}</span>
              </h2>
              <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
                See how TraderHub24 compares to traditional trading tools
              </p>
            </div>
            <div className="max-w-7xl mx-auto">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-4 text-left text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10">Feature</th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-gray-900">TraderHub24</th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">Excel</th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">Google Sheets</th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">Tradingview</th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">Your Notebook</th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">Others</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Trade Journal</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Advanced Trade Tracking</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Advanced analytics & reporting</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Real-time Price Updates</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Access From Everywhere</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Trade Sharing</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Advanced Strategie Management</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50 transition-colors group">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10">Stop/Loss - Take Profit Notifications</td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-12 lg:py-20 mt-12 md:mt-16 bg-orange-50 border border-orange-200 rounded-2xl">
          <div className="text-center max-w-3xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pages:landing.cta.title')}
            </h2>
            <p className="text-gray-600 text-sm md:text-base mb-8">
              {t('pages:landing.cta.description')}
            </p>
            <button
              onClick={handleCTAClick}
              className="px-8 py-3 md:py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-md text-sm md:text-base"
            >
              {selectedCTA.text}
            </button>
            <p className="text-xs md:text-sm text-gray-500 mt-4">
              {t('pages:landing.noCreditCard')}
            </p>
          </div>
        </section>
      </main>

      {/* Video Popup Modal */}
      {videoPopupSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setVideoPopupSrc(null)}
        >
          <button
            className="absolute top-4 right-4 z-10 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            onClick={() => setVideoPopupSrc(null)}
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div
            className="relative w-full max-w-5xl aspect-video rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={videoPopupSrc}
              autoPlay
              loop
              muted
              playsInline
              controls
              className="w-full h-full object-contain bg-black"
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer currentPage="home" onNavigate={onNavigate} />
    </div>
  );
}

export default LandingPage;

